/*
===============================================================================================================
                                 Copyright 2025-2026, Blue Yonder Group, Inc.
                                              All Rights Reserved

                                THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
                                            BLUE YONDER GROUP, INC.

                            The copyright notice above does not evidence any actual
                                 or intended publication of such source code.

===============================================================================================================
*/

import { AdminContext } from '@/contexts/admin-context';
import { AuthContext } from '@/contexts/auth-context';
import useIdle from '@/utilities/useIdle';
import {
  useContext, useEffect, useMemo, useState,
} from 'react';
import luiPortalUtilitiesManager from './lui-portal-utilities-manager';

const useInactiveSession = () => {
  const { getAdminFeature } = useContext(AdminContext);
  const { isUserLoggedIn, clearAuth, getRefreshAuth } = useContext(AuthContext);
  const isInactivityDetectionEnabled = getAdminFeature('inactivityDetection')?.enabled ?? false;
  const inactivityDetectionTimeout = getAdminFeature('inactivityDetection')?.detectionTimeoutMs ?? (9 * 60 * 1000);
  const isWidgetMode = luiPortalUtilitiesManager.isWidgetModeActive();
  const { isIdle } = useIdle(
    inactivityDetectionTimeout,
  );
  const [isPromptVisible, setIsPromptVisible] = useState(false);
  const [countdown, setCountdown] = useState(60); // 60 seconds countdown

  const shouldLogout = useMemo(() => countdown === 0, [countdown]);

  const dismissPrompt = async () => {
    setIsPromptVisible(false);
    setCountdown(60); // Reset countdown
    await getRefreshAuth(); // Refresh auth token if needed
  };

  useEffect(() => {
    if (!isWidgetMode) {
      if (isIdle && isUserLoggedIn && isInactivityDetectionEnabled) {
        setIsPromptVisible(true);
      }
    }
  }, [isIdle, isUserLoggedIn, isInactivityDetectionEnabled, isWidgetMode]);

  useEffect(() => {
    if (!isWidgetMode) {
      if (shouldLogout && isUserLoggedIn && isInactivityDetectionEnabled) {
        clearAuth();
      }
    }
  }, [shouldLogout, isUserLoggedIn, isInactivityDetectionEnabled, isWidgetMode, clearAuth]);

  useEffect(() => {
    let countdownInterval;
    // start countdown when the prompt becomes visible
    if (isPromptVisible) {
      setCountdown(60); // Reset countdown to 60 seconds
      countdownInterval = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(countdownInterval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(countdownInterval);
  }, [isPromptVisible]); // Reset countdown when the prompt is shown

  return {
    dismissPrompt,
    isPromptVisible,
    countdown,
    shouldLogout,
  };
};

export default useInactiveSession;
